import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Container,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, Person, Lock, Microsoft } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { login, isAuthenticated, error, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // Clear errors when inputs change
  useEffect(() => {
    clearError();
  }, [username, password, clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username || !password) {
      return;
    }

    setIsSubmitting(true);
    try {
      await login(username, password);
      // Navigation will happen via the useEffect above
    } catch (err) {
      // Error is handled by the auth context
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMicrosoftLogin = () => {
    // Route via API to avoid proxy mis-routing at frontend origin
    const next = encodeURIComponent((location.state as any)?.from?.pathname || '/dashboard');
    const apiBase = (import.meta.env.VITE_API_URL || '/api/v1').replace(/\/$/, '');
    const target = `${apiBase}/users/auth/azure-login/?next=${next}`;
    window.location.href = target;
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Quick login buttons for development/testing
  const quickLogin = async (testUsername: string) => {
    setUsername(testUsername);
    setPassword('testpass123');
    setIsSubmitting(true);
    try {
      await login(testUsername, 'testpass123');
    } catch (err) {
      // Error handled by context
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1e3a5f 0%, #3d5a7a 100%)',
        }}
      >
        <Card
          sx={{
            width: '100%',
            maxWidth: 450,
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            borderRadius: 2,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box
                component="img"
                src="/regenops-logo.png"
                alt="Re-GenOps"
                sx={{
                  height: 64,
                  mb: 3,
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <Typography variant="subtitle1" color="text.secondary">
                Generation Operations Management System
              </Typography>
            </Box>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                margin="normal"
                required
                disabled={isSubmitting}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                disabled={isSubmitting}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        disabled={isSubmitting}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={!username || !password || isSubmitting}
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  mb: 3,
                }}
              >
                {isSubmitting ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>

              <Button
                onClick={handleMicrosoftLogin}
                fullWidth
                variant="outlined"
                size="large"
                sx={{
                  py: 1.3,
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                Sign in with Microsoft
              </Button>
            </form>

            {/* Development Quick Login Buttons */}
            {import.meta.env.MODE === 'development' && (
              <>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  textAlign="center"
                  sx={{ mb: 2 }}
                >
                  Development Quick Login
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => quickLogin('test_worker')}
                    disabled={isSubmitting}
                  >
                    🧑‍🔧 Worker (test_worker)
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => quickLogin('test_supervisor')}
                    disabled={isSubmitting}
                  >
                    👨‍💼 Supervisor (test_supervisor)
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => quickLogin('test_issuer')}
                    disabled={isSubmitting}
                  >
                    🛡️ Issuer (test_issuer)
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => quickLogin('test_recipient')}
                    disabled={isSubmitting}
                  >
                    📋 Recipient (test_recipient)
                  </Button>
                </Box>
              </>
            )}

            {/* Footer */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Re-GenOps v1.0 • Generation Operations Management System
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Login; 